import styled from '@emotion/styled';
import { Icon, tokens } from '@hotjar/hotsauce';

import { useContext, createContext } from 'react';

const ListBoxItemContainer = styled.div<{ isSelected: boolean; isActive: boolean }>`
  flex-shrink: 0;
  overflow: hidden;
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
  focusable: true;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.spacer.x1};
  cursor: pointer;

  &:hover {
    background: ${tokens.color.bg_hover};
  }

  ${({ isSelected }) => isSelected && `background: ${tokens.color.bg_active};`};

  ${({ isActive }) =>
    isActive &&
    `
  ${tokens.focus()};
  z-index: 100;
`};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-shrink: 1;
  flex-grow: 1;
  overflow: hidden;
`;

const CheckContainer = styled.div`
  width: ${tokens.spacer.x2};
  height: ${tokens.spacer.x2};
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
`;

type ListBoxItemProps = {
  id: string;
  value: string;
  children: React.ReactNode;
};

export type SelectedValueContextValue = {
  selectedValue: unknown | undefined;
  activeId: string | undefined;
  onSelect: ((value: string) => void) | undefined;
  onOptionMouseDown: (() => void) | undefined;
};

export const SelectedValueContext = createContext<SelectedValueContextValue>({
  selectedValue: undefined,
  activeId: undefined,
  onSelect: undefined,
  onOptionMouseDown: undefined,
});

export const ListBoxItem = ({ id, children, value }: ListBoxItemProps): JSX.Element => {
  const { selectedValue, onSelect, onOptionMouseDown, activeId } = useContext(SelectedValueContext);
  const isSelected = selectedValue === value;
  const isActive = activeId === id;
  return (
    <ListBoxItemContainer
      id={id}
      role="option"
      aria-selected={isSelected}
      isSelected={isSelected}
      isActive={isActive}
      onClick={() => onSelect?.(value)}
      onMouseDown={onOptionMouseDown}
    >
      <ContentContainer>{children}</ContentContainer>
      <CheckContainer>{isSelected && <Icon icon="selected" />}</CheckContainer>
    </ListBoxItemContainer>
  );
};
